import React, { useState, useEffect } from 'react'
import { X, Users, CheckSquare, UserPlus, Check, ChevronRight, Loader, Sparkles, Plus, CalendarClock, CalendarDays } from 'lucide-react'
import { apiClient, SetupStatus } from '../utils/api'
import { useFamily } from '../contexts/FamilyContext'
import toast from 'react-hot-toast'

interface SetupWizardProps {
  onClose: () => void
  onComplete: () => void
}

type WizardStep = 'welcome' | 'kids' | 'chores' | 'schedule' | 'coparent' | 'complete'

export const SetupWizard: React.FC<SetupWizardProps> = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('welcome')
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const { members, refresh: refreshFamily } = useFamily()

  // Inline add-kid form
  const [kidName, setKidName] = useState('')
  const [kidEmoji, setKidEmoji] = useState('')
  const [addingKid, setAddingKid] = useState(false)
  const [justAddedKid, setJustAddedKid] = useState<{ id: string; display_name: string } | null>(null)

  // Inline first-chore assignment
  const [starterChores, setStarterChores] = useState<Array<{ id: string; name: string; base_points?: number }>>([])
  const [selectedChoreId, setSelectedChoreId] = useState('')
  const [assigningChore, setAssigningChore] = useState(false)
  const [firstChoreAssigned, setFirstChoreAssigned] = useState(false)

  // Chore rotation schedule choice
  const [scheduleChoice, setScheduleChoice] = useState<'daily' | 'weekly' | 'individual' | null>(null)
  // Per-chore assignment rules are offered once the family has chore_rules on.
  const [rulesEnabled, setRulesEnabled] = useState(false)
  useEffect(() => {
    apiClient.get('/api/family/features')
      .then((data: any) => {
        const features = Array.isArray(data) ? data : data?.data || []
        setRulesEnabled(features.some((f: any) => f.feature_key === 'chore_rules' && f.enabled === true))
      })
      .catch(() => setRulesEnabled(false))
  }, [])
  const [savingSchedule, setSavingSchedule] = useState(false)

  // Co-parent invite form
  const [coparentEmail, setCoparentEmail] = useState('')
  const [coparentName, setCoparentName] = useState('')
  const [coparentRole, setCoparentRole] = useState<'parent' | 'caretaker'>('parent')
  const [inviting, setInviting] = useState(false)

  const kids = members.filter(m => !m.is_parent)
  const KID_EMOJIS = ['😀', '🦄', '🚀', '⚽', '🐶', '🌟']

  useEffect(() => {
    loadSetupStatus()
    loadStarterChores()
  }, [])

  const loadSetupStatus = async () => {
    try {
      const status = await apiClient.getSetupStatus()
      setSetupStatus(status)
    } catch (error) {
      console.error('Failed to load setup status:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStarterChores = async () => {
    try {
      const chores = await apiClient.getParentChores()
      // Show a short, low-effort set of starter chores to assign first.
      setStarterChores((chores || []).slice(0, 6))
    } catch (error) {
      console.error('Failed to load chores:', error)
    }
  }

  const handleAddKid = async () => {
    if (!kidName.trim()) {
      toast.error('Please enter a name')
      return
    }

    setAddingKid(true)
    try {
      const created = await apiClient.createChild(kidName.trim(), { emoji: kidEmoji || undefined })
      await refreshFamily()
      setJustAddedKid({ id: created.id, display_name: created.display_name })
      setKidName('')
      setKidEmoji('')
      toast.success(`${created.display_name} added!`)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to add kid')
    } finally {
      setAddingKid(false)
    }
  }

  // Kid to receive the first chore: the one just added, else the first kid.
  const targetKid = justAddedKid || (kids[0] ? { id: kids[0].id, display_name: kids[0].display_name } : null)

  const handleAssignChore = async () => {
    const chore = starterChores.find(c => c.id === selectedChoreId)
    if (!chore || !targetKid) return

    setAssigningChore(true)
    try {
      const now = new Date()
      const dueDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      await apiClient.createAssignment({
        chore_id: chore.id,
        assigned_to: targetKid.id,
        points_offered: chore.base_points || 10,
        due_date: dueDate
      })
      setFirstChoreAssigned(true)
      toast.success(`Assigned "${chore.name}" to ${targetKid.display_name}`)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to assign chore')
    } finally {
      setAssigningChore(false)
    }
  }

  // Persist the chore-rotation cadence. 'skip' sets the opt-out marker so the
  // board is never auto-created; a frequency builds the starter board now.
  const saveSchedule = async (choice: 'daily' | 'weekly' | 'skip') => {
    setSavingSchedule(true)
    try {
      await apiClient.setChoreSchedule(choice)
      if (choice !== 'skip') {
        toast.success(`Chores will rotate ${choice}`)
        await refreshFamily()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save chore schedule')
    } finally {
      setSavingSchedule(false)
    }
  }

  const handleSkip = async () => {
    // On the schedule step, "Skip for now" is an explicit opt-out: record it so
    // the parent isn't surprised by an auto-built board later.
    if (currentStep === 'schedule') {
      await saveSchedule('skip')
    }
    moveToNextStep()
  }

  // Continue from the schedule step: if the parent picked a cadence, save it
  // first. If they picked nothing, leave it unset so the backend's daily
  // default still populates the board (better for activation than opting out).
  const handleContinue = async () => {
    if (currentStep === 'schedule') {
      if (scheduleChoice === 'individual') {
        // No starter rotation group: the parent sets each chore up instead.
        await saveSchedule('skip')
        toast.success('Open Chores, then Manage, to choose who does each chore and how often.')
      } else if (scheduleChoice) {
        await saveSchedule(scheduleChoice)
      }
    }
    moveToNextStep()
  }

  const moveToNextStep = () => {
    switch (currentStep) {
      case 'welcome':
        setCurrentStep('kids')
        break
      case 'kids':
        setCurrentStep('chores')
        break
      case 'chores':
        setCurrentStep('schedule')
        break
      case 'schedule':
        setCurrentStep('coparent')
        break
      case 'coparent':
        setCurrentStep('complete')
        break
      case 'complete':
        handleComplete()
        break
    }
  }

  const handleComplete = async () => {
    try {
      await apiClient.completeSetup()
      onComplete()
    } catch (error) {
      console.error('Failed to complete setup:', error)
      onComplete() // Still close on error
    }
  }

  const handleInviteCoparent = async () => {
    if (!coparentEmail.trim() || !coparentName.trim()) {
      toast.error('Please enter both email and name')
      return
    }

    setInviting(true)
    try {
      const result = await apiClient.inviteCoparent(coparentEmail.trim(), coparentName.trim(), coparentRole)
      toast.success(result.message)
      setCoparentEmail('')
      setCoparentName('')
      setCoparentRole('parent')
      moveToNextStep()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send invitation')
    } finally {
      setInviting(false)
    }
  }

  const steps = [
    { id: 'kids', label: 'Add Kids', icon: Users },
    { id: 'chores', label: 'Create Chores', icon: CheckSquare },
    { id: 'schedule', label: 'Schedule', icon: CalendarClock },
    { id: 'coparent', label: 'Invite Co-parent', icon: UserPlus },
  ]

  const getStepIndex = () => {
    switch (currentStep) {
      case 'welcome': return -1
      case 'kids': return 0
      case 'chores': return 1
      case 'schedule': return 2
      case 'coparent': return 3
      case 'complete': return 4
      default: return -1
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">
            {currentStep === 'welcome' && 'Welcome to HousePoints!'}
            {currentStep === 'kids' && 'Add Your Kids'}
            {currentStep === 'chores' && 'Create Chores'}
            {currentStep === 'schedule' && 'Chore Schedule'}
            {currentStep === 'coparent' && 'Invite Co-parent'}
            {currentStep === 'complete' && 'All Set!'}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress */}
        {currentStep !== 'welcome' && currentStep !== 'complete' && (
          <div className="px-6 py-3 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = getStepIndex() === index
                const isComplete = getStepIndex() > index
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      isComplete ? 'bg-green-500' : isActive ? 'bg-blue-600' : 'bg-gray-300'
                    }`}>
                      {isComplete ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <Icon className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className={`ml-2 text-sm hidden sm:block ${
                      isActive ? 'text-blue-600 font-medium' : 'text-gray-500'
                    }`}>
                      {step.label}
                    </span>
                    {index < steps.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-gray-400 mx-2 hidden sm:block" />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Welcome Step */}
          {currentStep === 'welcome' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Let's get you started!
              </h3>
              <p className="text-gray-600 mb-6">
                We'll help you set up your family in just a few quick steps. You can always customize things later.
              </p>
              <div className="space-y-3 text-left bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-gray-700">Add your kids</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckSquare className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-gray-700">Create some chores</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-gray-700">Invite your co-parent (optional)</span>
                </div>
              </div>
            </div>
          )}

          {/* Kids Step */}
          {currentStep === 'kids' && (
            <div>
              <p className="text-gray-600 mb-4">
                Add your kids so they can start earning points. You can add more later from the Settings menu.
              </p>

              {kids.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-medium text-gray-700">Current kids:</p>
                  {kids.map(kid => (
                    <div key={kid.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white">
                        {kid.emoji || kid.display_name[0]}
                      </div>
                      <span className="font-medium text-gray-900">{kid.display_name}</span>
                      <Check className="w-4 h-4 text-green-600 ml-auto" />
                    </div>
                  ))}
                </div>
              )}

              {/* Inline add-kid form */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
                <div className="flex gap-2 flex-wrap">
                  {KID_EMOJIS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setKidEmoji(kidEmoji === emoji ? '' : emoji)}
                      className={`w-9 h-9 rounded-full text-lg flex items-center justify-center border-2 transition-all ${
                        kidEmoji === emoji ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={kidName}
                    onChange={(e) => setKidName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !addingKid) handleAddKid() }}
                    placeholder="Kid's name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={addingKid}
                  />
                  <button
                    onClick={handleAddKid}
                    disabled={addingKid || !kidName.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {addingKid ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add
                  </button>
                </div>
              </div>

              {/* Inline first-chore assignment: turns the seeded board into the
                  actual core loop (kid -> chore -> points) before they leave. */}
              {targetKid && firstChoreAssigned ? (
                <div className="bg-green-50 rounded-lg p-4 flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <span className="text-green-800">{targetKid.display_name} has their first chore. They can complete it and you approve to award points.</span>
                </div>
              ) : targetKid && starterChores.length > 0 ? (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Give {targetKid.display_name} their first chore:
                  </p>
                  <div className="space-y-2 mb-3">
                    {starterChores.map(chore => (
                      <label
                        key={chore.id}
                        className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                          selectedChoreId === chore.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="starter-chore"
                          checked={selectedChoreId === chore.id}
                          onChange={() => setSelectedChoreId(chore.id)}
                          className="text-blue-600"
                        />
                        <span className="text-gray-900 flex-1">{chore.name}</span>
                        <span className="text-sm text-gray-500">{chore.base_points || 10} pts</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={handleAssignChore}
                    disabled={assigningChore || !selectedChoreId}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {assigningChore ? <Loader className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
                    Assign chore
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Chores Step */}
          {currentStep === 'chores' && (
            <div>
              <p className="text-gray-600 mb-4">
                Your account comes with a starter set of chores ready to go. Add, edit, or remove them anytime to fit your family.
              </p>

              {firstChoreAssigned ? (
                <div className="bg-green-50 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <span className="text-green-800">First chore assigned. Your kid can complete it and earn points once you approve.</span>
                </div>
              ) : (
                <div className="bg-green-50 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <span className="text-green-800">Starter chores are ready. Assign them to your kids from the dashboard whenever you like.</span>
                </div>
              )}

              <a
                href="/dashboard/chores"
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors"
              >
                <CheckSquare className="w-4 h-4 inline mr-2" />
                Customize chores
              </a>
            </div>
          )}

          {/* Schedule Step */}
          {currentStep === 'schedule' && (
            <div>
              <p className="text-gray-600 mb-4">
                Want HousePoints to put chores on your kids' boards automatically? Pick how often they should rotate. You can change or turn this off anytime from Rotations.
              </p>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setScheduleChoice('daily')}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    scheduleChoice === 'daily' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CalendarDays className={`w-6 h-6 mt-0.5 ${scheduleChoice === 'daily' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Daily</p>
                    <p className="text-sm text-gray-600">Chores are assigned fresh every day. Best for routines like dishes, pets, and tidying.</p>
                  </div>
                  {scheduleChoice === 'daily' && <Check className="w-5 h-5 text-blue-600 ml-auto" />}
                </button>

                <button
                  type="button"
                  onClick={() => setScheduleChoice('weekly')}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    scheduleChoice === 'weekly' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CalendarClock className={`w-6 h-6 mt-0.5 ${scheduleChoice === 'weekly' ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">Weekly</p>
                    <p className="text-sm text-gray-600">Chores rotate once a week. A lighter load that's easy to keep up with.</p>
                  </div>
                  {scheduleChoice === 'weekly' && <Check className="w-5 h-5 text-blue-600 ml-auto" />}
                </button>

                {rulesEnabled && (
                  <button
                    type="button"
                    onClick={() => setScheduleChoice('individual')}
                    className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      scheduleChoice === 'individual' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className={`w-6 h-6 mt-0.5 ${scheduleChoice === 'individual' ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-900">Set up chores individually</p>
                      <p className="text-sm text-gray-600">Choose per chore: every kid does it, a team shares it, kids take turns in your order, or it is open to claim.</p>
                    </div>
                    {scheduleChoice === 'individual' && <Check className="w-5 h-5 text-blue-600 ml-auto" />}
                  </button>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Prefer to assign chores yourself? Choose "Skip for now" and nothing will be auto-assigned.
              </p>
            </div>
          )}

          {/* Co-parent Step */}
          {currentStep === 'coparent' && (
            <div>
              <p className="text-gray-600 mb-4">
                Invite another adult to help manage your family. Choose their access level.
              </p>

              {setupStatus?.steps.has_coparent ? (
                <div className="bg-green-50 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <span className="text-green-800">You already have a co-parent!</span>
                </div>
              ) : setupStatus?.steps.coparent_invited ? (
                <div className="bg-blue-50 rounded-lg p-4 mb-4 flex items-center gap-3">
                  <UserPlus className="w-6 h-6 text-blue-600" />
                  <span className="text-blue-800">Invitation sent! Waiting for them to accept.</span>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setCoparentRole('parent')}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                          coparentRole === 'parent'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="font-medium">Parent</div>
                        <div className="text-xs opacity-70">Full access</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCoparentRole('caretaker')}
                        className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                          coparentRole === 'caretaker'
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                      >
                        <div className="font-medium">Caretaker</div>
                        <div className="text-xs opacity-70">Limited access</div>
                      </button>
                    </div>
                    {coparentRole === 'caretaker' && (
                      <p className="text-xs text-amber-600 mt-1">Caretakers can verify chores and manage meals. Adjust permissions later in Settings.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Their Name
                    </label>
                    <input
                      type="text"
                      value={coparentName}
                      onChange={(e) => setCoparentName(e.target.value)}
                      placeholder="e.g., Sarah"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={inviting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Their Email
                    </label>
                    <input
                      type="email"
                      value={coparentEmail}
                      onChange={(e) => setCoparentEmail(e.target.value)}
                      placeholder="partner@email.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={inviting}
                    />
                  </div>
                  <button
                    onClick={handleInviteCoparent}
                    disabled={inviting || !coparentEmail.trim() || !coparentName.trim()}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {inviting ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Complete Step */}
          {currentStep === 'complete' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                You're all set!
              </h3>
              <p className="text-gray-600 mb-6">
                Your family is ready to start using HousePoints. You can always adjust settings and add more members later.
              </p>
              <div className="bg-blue-50 rounded-xl p-4 text-left">
                <p className="font-medium text-blue-900 mb-2">Quick tips:</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>Assign chores daily or set up automatic rotations</li>
                  <li>Kids can see their tasks on their own dashboard</li>
                  <li>Verify completed chores to award points</li>
                  <li>Set up rewards for kids to redeem their points</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between">
          {currentStep !== 'welcome' && currentStep !== 'complete' ? (
            <>
              <button
                onClick={handleSkip}
                disabled={savingSchedule}
                className="text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
              >
                Skip for now
              </button>
              <button
                onClick={handleContinue}
                disabled={savingSchedule}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {savingSchedule ? <Loader className="w-4 h-4 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : currentStep === 'welcome' ? (
            <button
              onClick={moveToNextStep}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              Let's Go!
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleComplete}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Start Using HousePoints
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default SetupWizard
